import { Component, type ErrorInfo, type ReactNode } from 'react';
import { reportError } from '../lib/telemetry';

interface Props {
  children: ReactNode;
  /** Étiquette de contexte pour la télémétrie (ex. `panda-panic`). */
  context?: string;
  /** Repli affiché en cas d'erreur ; défaut = message panda générique. */
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
}

/**
 * Frontière d'erreur React réutilisable (spec 15 §8 — Phase 5). Isole un
 * sous-arbre (typiquement l'aire d'un jeu 3D) : un plantage au rendu ou l'échec
 * de chargement de son chunk lazy n'écrase plus toute la SPA. L'erreur est
 * signalée via la télémétrie basique puis un repli s'affiche.
 *
 * NB : une frontière React ne capte QUE les erreurs de rendu / cycle de vie.
 * Les erreurs ASYNCHRONES (boucle `requestAnimationFrame` du jeu) échappent à
 * React et sont gardées séparément côté jeu (cf. `games/panda-panic/loopGuard`).
 */
export class ErrorBoundary extends Component<Props, State> {
  override state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    reportError(error, {
      context: this.props.context ?? 'error-boundary',
      componentStack: info.componentStack ?? undefined,
    });
  }

  override render(): ReactNode {
    if (!this.state.hasError) return this.props.children;
    if (this.props.fallback !== undefined) return this.props.fallback;
    return (
      <div role="alert" className="grid place-content-center gap-2 p-8 text-center text-ink-300">
        <p className="font-display text-xl text-ink-100">Oups, un pépin technique 🐼</p>
        <p>Recharge la page pour réessayer.</p>
      </div>
    );
  }
}
